
import React, { useState } from 'react';
import { City } from '../../data/types/city-types';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Maximize2, Minimize2, Users, Church, MapPin, Info, Globe, Heart, DollarSign } from 'lucide-react';
import { useLanguage } from '../../contexts/language';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import YouTubeEmbed from '../video/YouTubeEmbed';
import FundingApplicationForm from './FundingApplicationForm';
import DonationForm from './DonationForm';

interface CitySidebarProps {
  city: City;
  onClose: () => void;
}

const CitySidebar: React.FC<CitySidebarProps> = ({ city, onClose }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [isMaximized, setIsMaximized] = useState(false);

  const getCityVideoId = (cityName: string): string => {
    const cityVideos: { [key: string]: string } = {
      'Tuxtla Gutiérrez': 'QqV-T-yiKso',
      'San Cristóbal de las Casas': 'Iq6x_fc4zxM',
      'Tapachula': 'y9YYtIPijAc',
      'Pachuca': 'dQb4-YZvxOs',
      'Tulancingo': 'xP4Xh8OBTjY',
    };
    
    return cityVideos[cityName] || 'dQw4w9WgXcQ';
  };

  const churchDensity = ((city.churches_count / city.population) * 10000).toFixed(2);

  const getPresenceColor = (presence: string): string => {
    switch(presence) {
      case 'High': return 'bg-green-500';
      case 'Medium': return 'bg-yellow-500';
      case 'Low': return 'bg-orange-500';
      case 'Unreached': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const MainContent = () => (
    <div className="flex-1 overflow-auto">
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="px-4 pt-2">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="funding">Apply for Funding</TabsTrigger>
            <TabsTrigger value="pledge">Pledge</TabsTrigger>
          </TabsList>
        </div>
        
        <div className="flex-1 overflow-auto">
          <TabsContent value="overview" className="p-4 flex-1">
            <div className="space-y-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <YouTubeEmbed 
                    videoId={getCityVideoId(city.name)} 
                    title={`Video about ${city.name}`}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="rounded-lg h-40 overflow-hidden mb-4">
              <img 
                src={city.image_url} 
                alt={city.name} 
                className="w-full h-full object-cover"
              />
            </div>
            
            <Card className="mb-4">
              <CardContent className="p-4">
                <p className="text-gray-700">
                  {city.description}
                </p>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <Users className="h-5 w-5 text-purple-600 mb-1" />
                  <span className="text-xs text-gray-500">{t('population')}</span>
                  <span className="text-lg font-bold text-gray-800">
                    {city.population.toLocaleString()}
                  </span>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <Church className="h-5 w-5 text-purple-600 mb-1" />
                  <span className="text-xs text-gray-500">{t('churches')}</span>
                  <span className="text-lg font-bold text-gray-800">
                    {city.churches_count}
                  </span>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <Globe className="h-5 w-5 text-purple-600 mb-1" />
                  <span className="text-xs text-gray-500">{t('churchDensity')}</span>
                  <span className="text-lg font-bold text-gray-800">
                    {churchDensity}
                  </span>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <Heart className="h-5 w-5 text-red-600 mb-1" />
                  <span className="text-xs text-gray-500">{t('unreached')}</span>
                  <span className="text-lg font-bold text-gray-800">
                    {city.unreached_percentage}%
                  </span>
                </CardContent>
              </Card>
            </div>
            
            <div className="mt-5 bg-gray-100 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t('churchPresence')}</span>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${getPresenceColor(city.church_presence)}`}></div>
                  <span className="text-sm font-bold">{t(city.church_presence.toLowerCase())}</span>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="funding" className="p-4">
            <FundingApplicationForm 
              city={city}
              onCancel={() => setActiveTab('overview')}
            />
          </TabsContent>

          <TabsContent value="pledge" className="p-4">
            <DonationForm 
              city={city}
              onCancel={() => setActiveTab('overview')}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
  
  return (
    <>
      <div className={`fixed bottom-0 left-0 right-0 z-10 bg-white rounded-t-xl shadow-xl lg:w-[400px] lg:left-auto lg:top-0 lg:bottom-0 lg:rounded-none lg:rounded-l-xl overflow-hidden flex flex-col lg:h-full ${isMaximized ? 'hidden' : ''}`}>
        <div className="px-4 py-3 bg-gradient-to-r from-purple-700 to-purple-500 text-white flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:text-white hover:bg-white/20" 
            onClick={onClose}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-bold">{city.name}</h2>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:text-white hover:bg-white/20"
            onClick={() => setIsMaximized(true)}
          >
            <Maximize2 className="h-5 w-5" />
          </Button>
        </div>
        
        <MainContent />
      </div>

      <Dialog open={isMaximized} onOpenChange={setIsMaximized}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <div className="px-4 py-3 bg-gradient-to-r from-purple-700 to-purple-500 text-white flex items-center justify-between rounded-t-lg">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:text-white hover:bg-white/20" 
              onClick={onClose}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-bold">{city.name}</h2>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-white/20"
              onClick={() => setIsMaximized(false)}
            >
              <Minimize2 className="h-5 w-5" />
            </Button>
          </div>
          
          <MainContent />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CitySidebar;
